import { ReactNode } from "react";
import { Monitor } from "lucide-react";
import { useIsMobile } from "@/hooks/useMobile";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";

/**
 * Bloqueia construtores (flow builder, email editor, template editor, stepper)
 * em viewport < 768 px e mostra mensagem orientando o usuário a abrir em
 * tela maior. Editores drag-drop não funcionam bem em telas pequenas,
 * preferimos avisar a entregar uma experiência ruim.
 *
 * Uso recomendado (gate):
 *   export default function FlowBuilder() {
 *     return (
 *       <MobileBlockerGate featureName="Editor de Fluxos">
 *         <FlowBuilderImpl />
 *       </MobileBlockerGate>
 *     );
 *   }
 */
interface MobileBlockerProps {
  featureName: string;
  description?: string;
}

export function MobileBlocker({ featureName, description }: MobileBlockerProps) {
  const [, navigate] = useLocation();
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
      <div className="rounded-full bg-muted p-4 mb-4">
        <Monitor className="h-10 w-10 text-muted-foreground" />
      </div>
      <h2 className="text-lg font-semibold mb-2">{featureName} indisponível em telas pequenas</h2>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {description ??
          "Este editor precisa de mais espaço de tela. Abra num tablet ou computador para continuar editando."}
      </p>
      <Button variant="outline" onClick={() => navigate("/")}>
        Voltar ao Dashboard
      </Button>
    </div>
  );
}

/**
 * Wrapper-componente para gating de editores. Em mobile, renderiza
 * MobileBlocker; do tablet pra cima, renderiza children normalmente.
 * Padrão preferido em vez do hook `useMobileBlockerFor` (que retornava JSX
 * de hook — anti-pattern que dificultava early returns idiomáticos).
 */
interface MobileBlockerGateProps extends MobileBlockerProps {
  children: ReactNode;
}

export function MobileBlockerGate({ featureName, description, children }: MobileBlockerGateProps) {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileBlocker featureName={featureName} description={description} />;
  return <>{children}</>;
}
